import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PillCelebrationEffect } from '../../../components/Celebration/PillCelebrationEffect';
import { PillSadnessEffect } from '../../../components/Celebration/PillSadnessEffect';
import { CelebrationPillProps } from '../../../presentation/GamePresentationTypes';
import { arenaAudio } from '../../../services/ArenaAudioService';

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className = 'text-gray-300/80' }) => (
  <motion.span
    animate={{ x: [0, 4, 0] }}
    transition={{
      duration: 1.2,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
    className="inline-flex items-center justify-center shrink-0"
  >
    <svg
      className={`w-5 h-5 sm:w-6 sm:h-6 ${className} transition-all shrink-0 select-none pointer-events-none`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </motion.span>
);

export const HeartsCelebrationPill: React.FC<CelebrationPillProps> = ({
  gameState,
  mySeat,
  onDismiss,
  bostonResult,
}) => {
  const userSeat = mySeat !== null && mySeat !== undefined ? Number(mySeat) : 0;

  // Authoritative points taken this hand
  const seatPoints = Array.isArray(gameState?.seatPoints)
    ? gameState.seatPoints
    : (Array.isArray((gameState as any)?.pointsTaken)
      ? (gameState as any).pointsTaken
      : [0, 0, 0, 0]);

  const userHandPoints = Number(seatPoints[userSeat]) || 0;

  // Authoritative Queen of Spades status
  const queenTakenSeat = gameState?.queenOfSpadesTakenBySeat !== undefined && gameState.queenOfSpadesTakenBySeat !== null
    ? Number(gameState.queenOfSpadesTakenBySeat)
    : (Number((gameState.gameExt as any)?.queenOfSpadesTakenBySeat) ?? null);
  const didUserTakeQueen = queenTakenSeat === userSeat;

  // Authoritative Jack of Diamonds status
  const rawJackSeat = gameState?.jackOfDiamondsTakenBySeat !== undefined && gameState.jackOfDiamondsTakenBySeat !== null
    ? Number(gameState.jackOfDiamondsTakenBySeat)
    : (Number((gameState?.gameExt as any)?.jackOfDiamondsTakenBySeat) ?? null);
  const didUserTakeJack = rawJackSeat !== null && rawJackSeat === userSeat;

  // Authoritative Moon Shot status
  const rawMoonSeat = gameState?.shotTheMoonSeat ?? (gameState as any)?.shotMoonSeat ?? (gameState?.gameExt as any)?.shotTheMoonSeat;
  const shotMoonSeat = rawMoonSeat !== undefined && rawMoonSeat !== null && Number(rawMoonSeat) >= 0 ? Number(rawMoonSeat) : null;
  const didUserShootMoon = shotMoonSeat !== null && shotMoonSeat === userSeat;
  const didSomeoneShootMoon = shotMoonSeat !== null;

  // Moon rule & pending status
  const currentRule = (gameState?.shootTheMoonRule ?? (gameState?.gameExt as any)?.shootTheMoonRule ?? 'CHOICE') as string;
  const serverChoice = (gameState?.shootTheMoonChoice ?? (gameState?.gameExt as any)?.shootTheMoonChoice) as string | null;
  const serverPoints = Number(gameState?.shootTheMoonPoints ?? (gameState?.gameExt as any)?.shootTheMoonPoints ?? 26);
  const moonChoicePending = Boolean(gameState?.moonChoicePending ?? (gameState?.gameExt as any)?.moonChoicePending);

  const [selectedChoice, setSelectedChoice] = useState<'MINUS_SELF' | 'ADD_OTHERS'>(
    serverChoice === 'ADD_OTHERS' && currentRule === 'ADD_TO_OTHERS' ? 'ADD_OTHERS' : 'MINUS_SELF'
  );
  const [selectedPoints, setSelectedPoints] = useState<number>(serverPoints || 26);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [choiceSubmitted, setChoiceSubmitted] = useState(!moonChoicePending && Boolean(serverChoice));

  // Sync state if server updates
  React.useEffect(() => {
    if (serverChoice) {
      setSelectedChoice(serverChoice === 'ADD_OTHERS' && currentRule === 'ADD_TO_OTHERS' ? 'ADD_OTHERS' : 'MINUS_SELF');
    }
    if (serverPoints) {
      setSelectedPoints(serverPoints);
    }
    if (!moonChoicePending && serverChoice) {
      setChoiceSubmitted(true);
    }
  }, [serverChoice, serverPoints, moonChoicePending, currentRule]);

  const handleConfirmMoonChoice = async () => {
    const tableId = gameState?.tableId || (gameState as any)?.id;
    if (!tableId) return;
    setIsSubmitting(true);
    try {
      const { chooseMoonAction } = await import('../../../state/ArenaGameActionsService');
      await chooseMoonAction(tableId, userSeat, selectedChoice, selectedPoints);
      setChoiceSubmitted(true);
    } catch (err) {
      console.error('[HeartsCelebrationPill] Failed to apply moon choice', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Audio trigger
  const playedAudioRef = React.useRef(false);
  React.useEffect(() => {
    if (playedAudioRef.current) return;
    playedAudioRef.current = true;
    try {
      if (didUserShootMoon) {
        arenaAudio.playBostonPrizeTone();
      } else if (didSomeoneShootMoon) {
        arenaAudio.playDescending5ToneSet();
      } else if (didUserTakeJack && !didUserTakeQueen) {
        arenaAudio.playAscending5ToneSuccess();
      } else if (didUserTakeQueen) {
        arenaAudio.playDescending5ToneSet();
      } else if (userHandPoints <= 0) {
        arenaAudio.playAscending5ToneSuccess();
      } else {
        arenaAudio.playCardPlay();
      }
    } catch {}
  }, [didUserShootMoon, didSomeoneShootMoon, didUserTakeJack, didUserTakeQueen, userHandPoints]);

  // 1. Moon Shot Outcome
  if (didSomeoneShootMoon) {
    const shooterName = gameState?.seats?.[shotMoonSeat]?.name || `Seat ${shotMoonSeat + 1}`;
    const shooterChoice = gameState?.shootTheMoonChoice ?? (gameState?.gameExt as any)?.shootTheMoonChoice;
    const shooterPoints = gameState?.shootTheMoonPoints ?? (gameState?.gameExt as any)?.shootTheMoonPoints ?? 26;
    const moonDesc = shooterChoice === 'MINUS_SELF'
      ? `-${shooterPoints} to Self`
      : `+${shooterPoints} to Opponents`;

    return (
      <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[95vw]">
        {didUserShootMoon ? (
          <div className="relative">
            <PillCelebrationEffect />
            {currentRule === 'CHOICE' && !choiceSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
                transition={{
                  y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                  opacity: { duration: 0.25 },
                  scale: { duration: 0.25 },
                }}
                className="relative z-20 flex flex-col sm:flex-row items-center gap-2.5 sm:gap-4 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-2xl bg-gradient-to-r from-rose-500 via-pink-600 to-rose-700 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white"
              >
                <div className="flex items-center gap-2 sm:gap-2.5">
                  <span className="text-lg sm:text-xl leading-none">🌙</span>
                  <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                    Shot The Moon!
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2">
                  <div className="inline-flex rounded-xl bg-black/40 p-0.5 border border-white/20">
                    <button
                      type="button"
                      onClick={() => setSelectedChoice('MINUS_SELF')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                        selectedChoice === 'MINUS_SELF'
                          ? 'bg-rose-500 text-white shadow'
                          : 'text-white/70 hover:text-white'
                      }`}
                    >
                      - {selectedPoints} Self
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedChoice('ADD_OTHERS')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                        selectedChoice === 'ADD_OTHERS'
                          ? 'bg-rose-500 text-white shadow'
                          : 'text-white/70 hover:text-white'
                      }`}
                    >
                      + {selectedPoints} Opponents
                    </button>
                  </div>

                  <select
                    value={selectedPoints}
                    onChange={(e) => setSelectedPoints(Number(e.target.value))}
                    className="px-2.5 py-1.5 text-xs font-bold rounded-lg bg-black/60 border border-white/20 text-white focus:outline-none focus:border-rose-400 cursor-pointer"
                    aria-label="Moon points amount"
                  >
                    <option value={26}>26 Pts</option>
                    <option value={50}>50 Pts</option>
                    <option value={20}>20 Pts</option>
                    <option value={30}>30 Pts</option>
                  </select>

                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.95 }}
                    onClick={handleConfirmMoonChoice}
                    disabled={isSubmitting}
                    className="py-1.5 px-3.5 rounded-lg font-black text-xs uppercase tracking-wider bg-white hover:bg-slate-100 text-rose-700 border border-white/40 shadow cursor-pointer transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isSubmitting ? '...' : choiceSubmitted ? 'Applied ✓' : 'Confirm'}
                  </motion.button>
                </div>

                <motion.button
                  type="button"
                  whileTap={{ scale: 0.95 }}
                  onClick={onDismiss}
                  aria-label="Next"
                  className="py-2 px-3.5 sm:px-4.5 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider bg-white/20 hover:bg-white/30 text-white border border-white/30 cursor-pointer transition-all flex items-center gap-1 active:scale-95"
                >
                  <ChevronRightIcon className="text-white" />
                </motion.button>
              </motion.div>
            ) : (
              <motion.button
                type="button"
                onClick={onDismiss}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.96 }}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
                transition={{
                  y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                  opacity: { duration: 0.25 },
                  scale: { duration: 0.25 },
                }}
                aria-label="Next"
                className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-rose-500 via-pink-600 to-rose-700 hover:from-rose-400 hover:via-pink-500 hover:to-rose-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
              >
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <span className="text-base sm:text-xl leading-none">🌙</span>
                  <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                    Shot The Moon! ({currentRule === 'SUBTRACT_FROM_SELF' ? `-${serverPoints} Self` : `+${serverPoints} Opponents`})
                  </span>
                </div>
                <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
              </motion.button>
            )}
          </div>
        ) : (
          <div className="relative">
            <PillSadnessEffect variant="set" />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-red-500 via-rose-600 to-red-700 hover:from-red-400 hover:via-rose-500 hover:to-red-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <span className="text-base sm:text-xl leading-none">💥</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  {shooterName} Shot The Moon! ({moonDesc})
                </span>
              </div>
              <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        )}
      </div>
    );
  }

  // 2. User took Jack of Diamonds and Queen of Spades
  if (didUserTakeJack && didUserTakeQueen) {
    return (
      <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
        <div className="relative">
          <PillCelebrationEffect />
          <motion.button
            type="button"
            onClick={onDismiss}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
              opacity: { duration: 0.25 },
              scale: { duration: 0.25 },
            }}
            aria-label="Next"
            className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-400 hover:via-amber-500 hover:to-orange-500 shadow-[0_10px_30px_rgba(245,158,11,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <span className="text-base sm:text-xl leading-none">👑♦</span>
              <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
                Queen (+13) but Jack of Diamonds (-10)! ({userHandPoints > 0 ? `+${userHandPoints}` : userHandPoints} Net)
              </span>
            </div>
            <ChevronRightIcon className="text-amber-100/90 group-hover:text-white group-hover:translate-x-0.5" />
          </motion.button>
        </div>
      </div>
    );
  }

  // 3. User took Jack of Diamonds (-10 bonus points)
  if (didUserTakeJack) {
    return (
      <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
        <div className="relative">
          <PillCelebrationEffect />
          <motion.button
            type="button"
            onClick={onDismiss}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            initial={{ opacity: 0, scale: 0.75 }}
            animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
              opacity: { duration: 0.25 },
              scale: { duration: 0.25 },
            }}
            aria-label="Next"
            className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-700 hover:from-emerald-400 hover:via-emerald-500 hover:to-teal-600 shadow-[0_10px_30px_rgba(168,85,247,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <span className="text-base sm:text-xl leading-none">♦</span>
              <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                Snagged Jack of Diamonds! (-10 Pts)
              </span>
            </div>
            <ChevronRightIcon className="text-emerald-100/90 group-hover:text-white group-hover:translate-x-0.5" />
          </motion.button>
        </div>
      </div>
    );
  }

  // 4. Clean Hand (0 Points Taken) - Celebratory!
  if (userHandPoints === 0) {
    return (
      <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
        <div className="relative">
          <PillCelebrationEffect />
          <motion.button
            type="button"
            onClick={onDismiss}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            initial={{ opacity: 0, scale: 0.75 }}
            animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
              opacity: { duration: 0.25 },
              scale: { duration: 0.25 },
            }}
            aria-label="Next"
            className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-700 hover:from-emerald-400 hover:via-emerald-500 hover:to-teal-600 shadow-[0_10px_30px_rgba(16,185,129,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <span className="text-base sm:text-xl leading-none">♥</span>
              <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                Clean Hand! 0 Points Taken
              </span>
            </div>
            <ChevronRightIcon className="text-emerald-100/90 group-hover:text-white group-hover:translate-x-0.5" />
          </motion.button>
        </div>
      </div>
    );
  }

  // 5. User Took Queen of Spades (+13 points) - Sadness effect
  if (didUserTakeQueen) {
    return (
      <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
        <div className="relative">
          <PillSadnessEffect variant="set" />
          <motion.button
            type="button"
            onClick={onDismiss}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
              opacity: { duration: 0.25 },
              scale: { duration: 0.25 },
            }}
            aria-label="Next"
            className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-red-500 via-rose-600 to-red-700 hover:from-red-400 hover:via-rose-500 hover:to-red-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <span className="text-base sm:text-xl leading-none">👑</span>
              <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                Took Queen of Spades! (+{userHandPoints} Pts)
              </span>
            </div>
            <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
          </motion.button>
        </div>
      </div>
    );
  }

  // 6. Default: User took some Hearts penalty points (> 0)
  return (
    <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
      <div className="relative">
        <PillSadnessEffect variant="opponents_bid" />
        <motion.button
          type="button"
          onClick={onDismiss}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
          transition={{
            y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
            opacity: { duration: 0.25 },
            scale: { duration: 0.25 },
          }}
          aria-label="Next"
          className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-rose-500 via-pink-600 to-rose-700 hover:from-rose-400 hover:via-pink-500 hover:to-rose-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
        >
          <div className="flex items-center gap-2.5 sm:gap-3">
            <span className="text-base sm:text-xl leading-none">♥</span>
            <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              +{userHandPoints} Penalty {userHandPoints === 1 ? 'Point' : 'Points'} Taken
            </span>
          </div>
          <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
        </motion.button>
      </div>
    </div>
  );
};

